import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';

function NavBar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const hasFamily = currentUser?.familyIds?.length > 0;
  const isOnboarding = currentUser && !hasFamily;
  const onboardUrl = `/onboard?intent=${currentUser?.signupIntent || 'create'}`;

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/home';
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      const profileDropdown = event.target.closest('[data-profile-dropdown]');
      if (isProfileDropdownOpen && !profileDropdown) {
        setIsProfileDropdownOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  return (
    <nav className="bg-white dark:bg-[#0d1520] shadow-sm dark:shadow-none border-b border-gray-200 dark:border-white/10 relative z-[10000]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img src="/images/app-icon.png" alt="Lineage Bible Logo" className="h-10 w-10 mr-2 rounded-xl" />
              <h1 className="text-2xl font-bold text-primary-600">Lineage Bible</h1>
            </Link>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center space-x-8">
            {!isOnboarding && (
              <>
                <Link to="/record" className="text-gray-700 dark:text-white/70 hover:text-primary-600 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium">
                  Record
                </Link>
                <Link to="/listen" className="text-gray-700 dark:text-white/70 hover:text-primary-600 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium">
                  Listen
                </Link>
                <Link to="/family" className="text-gray-700 dark:text-white/70 hover:text-primary-600 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium">
                  Family
                </Link>
                <Link to="/dashboard" className="text-gray-700 dark:text-white/70 hover:text-primary-600 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium">
                  Dashboard
                </Link>
                <Link to="/shop" className="text-gray-700 dark:text-white/70 hover:text-primary-600 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium">
                  Shop
                </Link>
                <a href="/help/" target="_blank" rel="noopener noreferrer" className="text-gray-700 dark:text-white/70 hover:text-primary-600 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium">
                  Help
                </a>
              </>
            )}

            {isOnboarding && (
              <Link
                to={onboardUrl}
                className="bg-cerulean text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-cerulean/90 transition-colors"
              >
                Continue Setup
              </Link>
            )}

            {currentUser ? (
              <div className="relative" data-profile-dropdown>
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center space-x-2 text-gray-700 dark:text-white/70 hover:text-primary-600 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  <span>{currentUser.displayName || currentUser.email}</span>
                  <svg className={`w-4 h-4 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Profile dropdown menu */}
                {isProfileDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white dark:bg-[#111827] rounded-lg shadow-lg border border-gray-200 dark:border-white/10 z-[60] min-w-48 overflow-hidden">
                    {!isOnboarding && (
                      <>
                        <Link
                          to="/dashboard"
                          className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-white/70 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                          onClick={() => setIsProfileDropdownOpen(false)}
                        >
                          Dashboard
                        </Link>
                        <Link
                          to="/profile"
                          className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-white/70 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                          onClick={() => setIsProfileDropdownOpen(false)}
                        >
                          Settings & Profile
                        </Link>
                        <a
                          href="/help/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-white/70 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                          onClick={() => setIsProfileDropdownOpen(false)}
                        >
                          Help Center
                        </a>
                      </>
                    )}
                    {currentUser.superAdmin === true && (
                      <Link
                        to="/super-admin"
                        className="w-full text-left px-4 py-3 text-sm font-medium text-red-600 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors flex items-center gap-2"
                        onClick={() => setIsProfileDropdownOpen(false)}
                      >
                        <ShieldCheckIcon className="w-4 h-4" /> Super Admin
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsProfileDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-white/70 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/auth" className="btn-primary text-sm">
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile menu button — only shown when not mid-onboarding (bottom nav handles normal nav) */}
          {isOnboarding && (
            <div className="md:hidden flex items-center gap-3">
              <Link
                to={onboardUrl}
                className="bg-cerulean text-white px-3 py-1.5 rounded-lg text-sm font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Continue Setup
              </Link>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-white/70 hover:text-primary-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 focus:outline-none"
              >
                <span className="sr-only">Open menu</span>
                {isMenuOpen ? (
                  <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu — onboarding state: only logout */}
      {isMenuOpen && isOnboarding && (
        <div className="md:hidden">
          <div className="px-4 pt-2 pb-4 bg-white dark:bg-[#0d1520] border-t border-gray-200 dark:border-white/10">
            {currentUser && (
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-white/50">Signed in as {currentUser.email}</p>
                {currentUser.superAdmin === true && (
                  <Link
                    to="/super-admin"
                    className="text-red-600 text-sm font-medium flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <ShieldCheckIcon className="w-4 h-4" /> Super Admin
                  </Link>
                )}
                <button
                  onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                  className="btn-secondary w-full text-sm"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default NavBar; 